import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { createApiClient } from '../api/client';
import { isWorkspaceAdmin } from '../api/types';
import { useAccessToken } from '../auth/useAuth';
import { useWorkspace } from './WorkspaceProvider';

/**
 * Workspaces whose finish needs nothing more this session: the façade answered `complete` or
 * `not-sponsored`. Never asked again, which is what keeps a finished sponsored workspace from paying
 * the finish's appcore reads on every visit (coordinator review of steward-app#1488, finding 7).
 * In memory, so a full reload asks once more; the ask is idempotent and cheap once complete.
 */
const settled = new Set<string>();

/** Workspaces with an ask in flight, so a remount does not send a second one alongside it. */
const inFlight = new Set<string>();

/** Test seam: forget what this page load has learned. */
export function resetSponsoredSetupFinishForTest(): void {
  settled.clear();
  inFlight.clear();
}

/**
 * Finishes a SPONSORED setup the accept could not (steward-app#1488, SCS 3.3).
 *
 * The façade's accept runs the finish step (the family's `advisor-connection` label, the shared
 * folder and its ACLs) right after appcore's accept commits, and never fails the accept over it. What
 * it could not do is done here: on entering the active workspace, until the façade says it is
 * `complete` (or `not-sponsored`), when the membership
 * says it came from a sponsorship and this person is its admin, the shell calls
 * `POST /sponsored-setup/finish`. The server decides everything from appcore's own records (an
 * active `sponsored` engagement), writes nothing for a workspace that has none, and is idempotent.
 *
 * ⚠️ `via` is a LABEL, used here only to decide whether to ASK, never whether anything is allowed.
 * An admin gate for the same reason: the engagement read needs `mem:manage`, so a non-admin would
 * only be refused.
 *
 * ⚠️ Mounted through {@link SponsoredSetupFinish} beside the Layout shell, INSIDE the assurance floor
 * gate (`src/router.tsx`). A sponsored workspace can be floored from creation, and a finish attempted
 * before the step-up wall would only be refused; this one runs after it. The gate EXEMPTS the
 * workspace picker (it renders its children there without probing), so this does not ask on the
 * picker, nor while a workspace switch is still landing: a floored refusal there would open the
 * step-up prompt over a page that is not the workspace.
 *
 * Fire and forget. A failure is not shown: nothing the family can do fixes it, and the next entry into
 * the workspace tries again.
 */
export function useSponsoredSetupFinish(): void {
  const token = useAccessToken();
  const { activeMembership, switchInFlight } = useWorkspace();
  const { pathname } = useLocation();
  const onPicker = pathname.replace(/\/+$/, '') === '/select-workspace';
  const workspaceId = activeMembership?.workspaceId ?? null;
  const eligible = !onPicker && !switchInFlight
    && !!activeMembership
    && activeMembership.status === 'active'
    && activeMembership.via?.kind === 'sponsorship'
    && isWorkspaceAdmin(activeMembership.roleIds);

  useEffect(() => {
    if (!token || !workspaceId || !eligible || settled.has(workspaceId) || inFlight.has(workspaceId)) return;
    inFlight.add(workspaceId);
    // The ambient workspace is bound at this call, in the same tick `workspaceId` was read from.
    createApiClient(token)
      .post<{ sponsoredSetup: string }>('/sponsored-setup/finish', {})
      .then(res => {
        // `incomplete` (or anything unexpected) stays unsettled: the next entry into this workspace
        // asks again.
        if (res?.sponsoredSetup === 'complete' || res?.sponsoredSetup === 'not-sponsored') settled.add(workspaceId);
      })
      .catch(() => { /* not settled; the next entry into this workspace asks again */ })
      .finally(() => { inFlight.delete(workspaceId); });
  }, [token, workspaceId, eligible]);
}

/** The hook as a render-nothing element, so the router can place it inside the floor gate. */
export function SponsoredSetupFinish(): null {
  useSponsoredSetupFinish();
  return null;
}
